import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { UserRole } from '../types';
import { defaultPathForRole } from '../services/roleHome';
import { ShieldAlert } from 'lucide-react';
import { roleLabel } from '../services/roleLabel';
import { isSuperTier } from '../services/roles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { currentUser } = useApp();
  const location = useLocation();

  if (!currentUser) {
    return null; // Login component handles redirection
  }

  // Super Admin bypasses all checks
  if (isSuperTier(currentUser.role)) {
      return <>{children}</>;
  }

  if (allowedRoles.includes(currentUser.role)) {
    return <>{children}</>;
  }

  // Wrong role for this page — this happens innocently all the time (a reload
  // or fresh deploy restores a URL from a previous session, or someone signs
  // into a different role in the same browser). Send them to their own home
  // page instead of dead-ending on a denial screen.
  const home = defaultPathForRole(currentUser.role);
  if (location.pathname !== home) {
    return <Navigate to={home} replace />;
  }

  // Only reachable if a role's own home route doesn't allow that role (a
  // route-config bug) — keeping the denial screen here breaks what would
  // otherwise be an infinite redirect loop.
  return (
    <div className="flex flex-col items-center justify-center h-[calc(100vh-100px)] text-center p-8 animate-in fade-in zoom-in">
      <div className="bg-red-50 p-6 rounded-full mb-4">
        <ShieldAlert size={64} className="text-red-500" />
      </div>
      <h1 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h1>
      <p className="text-slate-500 max-w-md mx-auto">
        Your account role ({roleLabel(currentUser.role).toUpperCase()}) does not have permission to view this secure area.
      </p>
      <p className="text-xs text-slate-400 mt-8">Security Event Logged.</p>
    </div>
  );
};
